'use strict'

const flow_ = require('lodash/flow')
const times_ = require('lodash/times')
const flatten_ = require('lodash/flatten')
const isEqual_ = require('lodash/isEqual')
const isPlainObject_ = require('lodash/isPlainObject')
const result = require('folktale/result')

const breadcrumbWrapper = require('dbsm-common/src/raven/withBreadcrumbs')
const sequence = require('dbsm-common/src/fp/sequence')

const queryResults = require('../helpers/queryResults')

const {
  cleanseRecord,
  createDraft,
  getRecordId,
  hasDraft,
  isRecordPristine
} = require('./records')
const {
  collectMissingRanges,
  freshScope,
  insertRecordAtIndex,
  overwriteRecordAtIndex,
  recordIndexById,
  removeRecordById,
  scopeHasRecord,
  setNewRecordMarkers,
  setSeedInScope,
  storeResultsInScope,
  updateNewRecordMarkers,
  updateNumMatchingRecords
} = require('./scopes')
const {
  doesRecordExist,
  freshStore,
  getDraftOrRecord,
  getScope,
  insertDraft,
  insertRecord,
  iterateScopes,
  readFromRecordsStore,
  removeDraft,
  removeRecord,
  resetDraft,
  setScope,
  storeQueryResults,
  updateRecordFields,
  updateScope
} = require('./store')
const { registrar } = require('./utils')

const WIXDATA_MAX_RECORD_LIMIT = 1000

module.exports = (wixDataProxy, collectionName, includes, Raven) => {
  let theStore = freshStore()
  const onChangeHandlers = []

  const service = ({ pageSize, sort, filter, seed, controllerId }) => {
    const scopeKey = JSON.stringify({ filter, sort })

    // pageAlign :: Integer -> Integer
    const pageAlignedFromIndex = from => from - from % pageSize

    // pageAlign :: (Integer, Integer) -> Integer
    const pageAlignedToIndex = (from, to) =>
      Math.ceil(
        (pageAlignedFromIndex(from) + (to - pageAlignedFromIndex(from))) /
          pageSize
      ) * pageSize

    // findRecords :: (Integer, Integer) -> Promise QueryResults
    const findRecords = async function(offset, length) {
      const wixDataQueryResults = await wixDataProxy.find(
        collectionName,
        filter,
        sort,
        offset,
        length,
        undefined,
        includes
      )

      return queryResults.fromWixDataQueryResults(wixDataQueryResults, offset)
    }

    // fetchMissingRanges :: (Scope, Integer, Integer) -> Promise ([QueryResults])
    const fetchMissingRanges = (scope, fromIndex, toIndex) => {
      const fetchFrom = pageAlignedFromIndex(fromIndex)
      const fetchTo = pageAlignedToIndex(fromIndex, toIndex)
      const missingRanges = collectMissingRanges(scope, fetchFrom, fetchTo)
      const correctedRanges = missingRanges
        .filter(([from, length]) => length <= WIXDATA_MAX_RECORD_LIMIT)
        .concat(
          flatten_(
            missingRanges
              .filter(([from, length]) => length > WIXDATA_MAX_RECORD_LIMIT)
              .map(([from, length]) =>
                times_(Math.ceil(length / WIXDATA_MAX_RECORD_LIMIT), x => [
                  from + x * WIXDATA_MAX_RECORD_LIMIT,
                  Math.min(
                    WIXDATA_MAX_RECORD_LIMIT,
                    length - x * WIXDATA_MAX_RECORD_LIMIT
                  )
                ])
              )
          )
        )

      return Promise.all(correctedRanges.map(range => findRecords(...range)))
    }

    // notify :: (Integer, Record, Record, ComponentId) -> Result [*] Error
    const notify = (before, after, componentIdToExclude) =>
      sequence(
        result,
        onChangeHandlers.map(handler =>
          result.try(() =>
            handler(
              cleanseRecord(before),
              cleanseRecord(after),
              componentIdToExclude
            )
          )
        )
      )

    // runApiCommand
    //   :: (([Store -> Store] -> (), Record, Integer, [*]) -> *, () -> *, Integer, [*]) -> *
    const runApiCommand = (f, g, index, ...args) => {
      const recordId = getScope(theStore, scopeKey).records[index]
      const record = getDraftOrRecord(theStore, recordId)

      return record.matchWith({
        Nothing: () => g(),
        Just: ({ value }) => {
          const update = (...flow) => {
            theStore = flow_(...flow)(theStore)
          }

          const notifyIfChanged = componentIdToExclude => {
            const updatedRecord = getDraftOrRecord(
              theStore,
              recordId
            ).getOrElse(null)
            if (!isEqual_(value, updatedRecord)) {
              return notify(value, updatedRecord, componentIdToExclude)
            } else {
              return result.Ok([])
            }
          }

          return f({ update, notifyIfChanged }, value, index, ...args)
        }
      })
    }

    // withRecordByIndex
    //   :: (([Store -> Store] -> (), Record, Integer, [*]) -> *, () -> *) -> (Integer, [*]) -> *
    const withRecordByIndex = (f, g) => (index, ...args) => {
      return runApiCommand(f, g, index, ...args)
    }

    // withRecordByIndexAsync
    //   :: (([Store -> Store] -> (), Record, Integer, [*]) -> *, () -> *) -> (Integer, [*]) -> *
    const withRecordByIndexAsync = (f, g) => async (index, ...args) => {
      return await runApiCommand(f, g, index, ...args)
    }

    // sanitise :: * -> *
    const sanitise = something => {
      const go = object =>
        Object.keys(object)
          .filter(key => key.startsWith('_'))
          .reduce(
            (acc, key) =>
              Object.assign(acc, {
                [key]: isPlainObject_(object[key])
                  ? go(object[key])
                  : object[key]
              }),
            {}
          )

      if (queryResults.Results.hasInstance(something)) {
        return something.map(({ items }) => {
          const sanitisedItems = items.map(item => go(item))

          return { items: sanitisedItems }
        })
      } else if (typeof something === 'object' && something) {
        return go(something)
      } else {
        return something
      }
    }

    // createBreadcrumb :: (String, [*]) -> Breadcrumb
    const createBreadcrumb = (fnName, args = []) => ({
      category: 'recordStore',
      level: 'info',
      message: `${fnName}(${args.map(a =>
        JSON.stringify(a)
      )}) (${controllerId})`,
      data: {
        scope: scopeKey
      }
    })

    const { withBreadcrumbs, withBreadcrumbsAsync } = breadcrumbWrapper(
      Raven,
      createBreadcrumb,
      sanitise
    )

    const isNewRecord = record =>
      record && !doesRecordExist(theStore, getRecordId(record))

    const api = {
      // getRecords :: (Integer, Integer) -> Promise QueryResults
      getRecords: withBreadcrumbsAsync(
        'getRecords',
        async (fromIndex, length) => {
          const storeReader = readFromRecordsStore(
            scopeKey,
            fromIndex,
            fromIndex + length
          )
          return storeReader(theStore).orElse(async () => {
            const missingRanges = await fetchMissingRanges(
              getScope(theStore, scopeKey),
              fromIndex,
              fromIndex + length
            )
            const notifyUpdatedRecords = (old, current) =>
              Object.keys(old.records)
                .filter(
                  key =>
                    isPlainObject_(current.records[key]) &&
                    current.records[key]._updatedDate >
                      old.records[key]._updatedDate
                )
                .forEach(key => notify(old.records[key], current.records[key]))
            const go = flow_(
              ...missingRanges.map(range =>
                flow_(
                  storeQueryResults(range),
                  updateScope(scopeKey, storeResultsInScope(range))
                )
              )
            )

            const oldStore = theStore
            theStore = go(theStore)
            notifyUpdatedRecords(oldStore, theStore)
            return storeReader(theStore, true)
          })
        }
      ),

      // seed :: () -> Promise ()
      seed: withBreadcrumbsAsync('seed', async () => {
        if (getScope(theStore, scopeKey).numSeedRecords === 0) {
          const queryResult = await findRecords(0, pageSize)
          const go = flow_(
            storeQueryResults(queryResult),
            updateScope(scopeKey, setSeedInScope(queryResult))
          )

          theStore = go(theStore)
        }
      }),

      // getSeedRecords :: () -> QueryResults
      getSeedRecords: withBreadcrumbs('getSeedRecords', () =>
        readFromRecordsStore(
          scopeKey,
          0,
          getScope(theStore, scopeKey).numSeedRecords,
          theStore,
          true
        )
      ),

      // getTotalCount :: () -> Integer
      getMatchingRecordCount: withBreadcrumbs(
        'getMatchingRecordCount',
        () => getScope(theStore, scopeKey).numMatchingRecords
      ),

      // removeRecord :: Integer -> Promise ()
      // recordId is a maybe because new records don't have IDs
      removeRecord: withBreadcrumbsAsync(
        'removeRecord',
        withRecordByIndexAsync(
          async ({ update, notifyIfChanged }, record, index) => {
            const recordId = getRecordId(record)
            if (!isNewRecord(record) && recordId) {
              await wixDataProxy.remove(collectionName, recordId)
            }
            const doUpdateMarkers = recordIndex => markers =>
              markers.filter(marker => marker !== recordIndex)
            const updateScopesFlow = iterateScopes(
              (scope, scopeKey) => updateScope(scopeKey, flow_(
                removeRecordById(recordId),
                updateNumMatchingRecords(x => x - 1),
                updateNewRecordMarkers(doUpdateMarkers(recordIndexById(recordId, scope)))
              )),
              scopeHasRecord(recordId)
            )(theStore)
            update(
              flow_(removeDraft(record), removeRecord(recordId)),
              ...updateScopesFlow
            )
            return notifyIfChanged()
          },
          () => {}
        )
      ),

      // reset :: () -> ()
      reset: withBreadcrumbs(
        'reset',
        () => (theStore = setScope(scopeKey, freshScope())(theStore))
      ),

      // newRecord :: (Integer, DefaultDraft) -> ()
      newRecord: withBreadcrumbs('newRecord', (index, defaultDraft) => {
        // There Can Only Be One new record
        const draft = createDraft(Object.create(null), defaultDraft)
        const go = flow_(
          insertDraft(draft),
          updateScope(
            scopeKey,
            flow_(
              updateNumMatchingRecords(x => x + 1),
              setNewRecordMarkers([index]),
              insertRecordAtIndex(index, draft)
            )
          )
        )

        theStore = go(theStore)
        notify(null, draft)
        return cleanseRecord(draft)
      }),

      // save :: Integer -> Record
      saveRecord: withBreadcrumbsAsync(
        'saveRecord',
        withRecordByIndexAsync(
          async ({ update, notifyIfChanged }, record, index) => {
            const postSaveRecord = await wixDataProxy.save(
              collectionName,
              cleanseRecord(record)
            )
            const doUpdateMarkers = markers =>
              markers.filter(marker => marker !== index)
            update(
              insertRecord(postSaveRecord),
              removeDraft(record),
              updateScope(
                scopeKey,
                flow_(
                  overwriteRecordAtIndex(index, postSaveRecord),
                  updateNewRecordMarkers(doUpdateMarkers)
                )
              )
            )
            notifyIfChanged()

            return cleanseRecord(postSaveRecord)
          },
          () => {}
        )
      ),

      // setFieldsValues :: (Integer, FieldsValues) -> ()
      setFieldsValues: withBreadcrumbs(
        'setFieldsValues',
        withRecordByIndex(
          (
            { update, notifyIfChanged },
            record,
            _,
            fieldValues,
            componentIdToExclude
          ) => {
            update(updateRecordFields(getRecordId(record), fieldValues))
            return notifyIfChanged(componentIdToExclude)
          },
          () => {}
        )
      ),

      // isPristine :: Integer -> Boolean
      isPristine: withBreadcrumbs(
        'isPristine',
        withRecordByIndex((_, record) => isRecordPristine(record), () => true)
      ),

      // hasDraft :: Integer -> Boolean
      hasDraft: withBreadcrumbs(
        'hasDraft',
        withRecordByIndex((_, record) => hasDraft(record), () => false)
      ),

      // isNewRecord :: Integer -> Boolean
      isNewRecord: withBreadcrumbs(
        'isNewRecord',
        withRecordByIndex((_, record) => isNewRecord(record), () => true)
      ),

      // resetDraft :: (Integer, DefaultDraft) -> ()
      resetDraft: withBreadcrumbs(
        'resetDraft',
        withRecordByIndex(
          ({ update, notifyIfChanged }, record, index, defaultDraft) => {
            update(
              isNewRecord(record)
                ? resetDraft(record, defaultDraft)
                : removeDraft(record)
            )
            return notifyIfChanged()
          },
          () => {}
        )
      )
    }

    if (!theStore.scopes[scopeKey]) {
      theStore = flow_(
        storeQueryResults(seed),
        setScope(scopeKey, freshScope()),
        updateScope(scopeKey, setSeedInScope(seed))
      )(theStore)
    }

    return api
  }

  service.onChange = registrar(onChangeHandlers)

  return service
}



// WEBPACK FOOTER //
// ./recordStore/service.js